/**
 * @voltxampere/spice — SPICE net name → schema Net resolver
 *
 * Sprint 16. SPICE net names are bare strings; schema nets carry
 * stable IDs separate from human-facing names. Resolution rules
 * (ADR-0029):
 *
 *   - SPICE `0` → schema `gnd` net (singleton, isGround=true).
 *     Name and id stay `gnd` for canonical export.
 *   - Numeric SPICE name (`1`, `2`, `15`) → schema id `n{N}`,
 *     name `n{N}`. Schema NetSchema rejects pure-digit names, so
 *     we have to prefix.
 *   - Alphanumeric (`vcc`, `out`, `node_a`) → schema id + name
 *     identical to SPICE. If the SPICE name violates the schema
 *     regex (must start with letter/underscore), we sanitize and
 *     warn.
 *
 * Round-trip: emit reverses the rules. `gnd` → `0`, `n{N}` →
 * `{N}`, alphanumeric kept verbatim.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export function createResolverContext() {
    return { netByName: new Map(), warnings: [] };
}
/**
 * Resolve a SPICE net name to a schema Net, creating one on first
 * sight. Subsequent calls with the same name return the same Net.
 */
export function resolveNet(ctx, spiceName) {
    if (spiceName === "0") {
        const existing = ctx.netByName.get("gnd");
        if (existing)
            return existing;
        const gnd = { id: "gnd", name: "gnd", isGround: true };
        ctx.netByName.set("gnd", gnd);
        return gnd;
    }
    // Numeric SPICE names — prefix with `n` so schema accepts them.
    if (/^\d+$/.test(spiceName)) {
        const schemaName = `n${spiceName}`;
        const existing = ctx.netByName.get(schemaName);
        if (existing)
            return existing;
        const net = { id: schemaName, name: schemaName, isGround: false };
        ctx.netByName.set(schemaName, net);
        return net;
    }
    // Alphanumeric — sanitize the leading character if needed.
    let schemaName = spiceName;
    if (!/^[a-zA-Z_]/.test(schemaName)) {
        const fixed = `n_${schemaName.replace(/[^a-zA-Z0-9_]/g, "_")}`;
        ctx.warnings.push(`Net name '${spiceName}' violates schema regex; renamed to '${fixed}'`);
        schemaName = fixed;
    }
    else if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(schemaName)) {
        const fixed = schemaName.replace(/[^a-zA-Z0-9_]/g, "_");
        ctx.warnings.push(`Net name '${spiceName}' contains illegal characters; sanitized to '${fixed}'`);
        schemaName = fixed;
    }
    const existing = ctx.netByName.get(schemaName);
    if (existing)
        return existing;
    const net = { id: schemaName, name: schemaName, isGround: false };
    ctx.netByName.set(schemaName, net);
    return net;
}
/**
 * Inverse: schema Net → SPICE net name. Ground always emits as
 * `0`; the `n{N}` prefix is stripped if the remainder is purely
 * digits, otherwise the schema name passes through.
 */
export function netToSpiceName(net) {
    if (net.isGround)
        return "0";
    const stripped = /^n(\d+)$/.exec(net.name);
    if (stripped !== null)
        return stripped[1] ?? net.name;
    return net.name;
}
export function listNets(ctx) {
    return Array.from(ctx.netByName.values());
}
//# sourceMappingURL=net-resolver.js.map